import { SubAPI } from './API'
import { ICompetence, ISkillTranslate } from 'types/cvsInterfaces'

/**
 * Competence API
 * @notExported
 */
class CompetenceAPI extends SubAPI {
  /**
   * Get all competences.
   *
   * @returns Array of competences.
   */
  public getAll(controller?: AbortController): Promise<ICompetence[]> {
    return this.api.get(`competences`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Update competence.
   *
   * @param data - Competence data.
   * @returns Updated competence.
   */
  public putCompetence(data: { competence: { id: number } }, controller?: AbortController): Promise<ICompetence> {
    return this.api.put(
      `competences/${data.competence.id}`,
      data,
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Create competence.
   *
   * @param body - Competence data.
   * @returns Created competence.
   */
  public createCompetence(
    body: { competence: string; language: string },
    controller?: AbortController
  ): Promise<ICompetence> {
    return this.api.post(`competences`, body, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Delete competence.
   *
   * @param id - Competence ID.
   * @returns Promise of competence deletion.
   */
  public delete(id: number, controller?: AbortController): Promise<void> {
    return this.api.delete(`competences/${id}`, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Merge competences.
   *
   * @param newSkillId - New competence ID.
   * @param oldSkills - Old competence IDs.
   * @returns Promise of competence merge.
   */
  public merge(newCompetenceId: number, oldCompetences: number[], controller?: AbortController): Promise<void> {
    return this.api.put(
      `competences/merge`,
      { newCompetenceId, oldCompetences },
      controller ? { signal: controller.signal } : undefined
    )
  }

  /**
   * Merge several groups of competences in one transaction: all or nothing.
   *
   * @param merges - Groups to merge.
   * @returns Promise of the merges.
   */
  public mergeBulk(
    merges: { newCompetenceId: number; oldCompetences: number[] }[],
    controller?: AbortController
  ): Promise<void> {
    return this.api.put(`competences/merge/bulk`, { merges }, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Translate competence.
   *
   * @param data - Competence translations data.
   * @returns Updated competence.
   */
  public translate(data: ISkillTranslate, controller?: AbortController): Promise<ICompetence> {
    return this.api.put(`competences/translate`, data, controller ? { signal: controller.signal } : undefined)
  }

  /**
   * Split competences.
   *
   * @param oldSkillId - Old competence ID.
   * @param newSkillIds - New competence IDs.
   * @returns Promise of competence split.
   */
  public split(oldCompetenceId: number, newCompetenceIds: number[], controller?: AbortController): Promise<void> {
    return this.api.put(
      'competences/split',
      { oldCompetenceId, newCompetenceIds },
      controller ? { signal: controller.signal } : undefined
    )
  }
}

export const competenceAPI = new CompetenceAPI()
